/**
 * ============================================================
 * Email Signup Popup — JavaScript
 * ============================================================
 * Standalone. No modifications to postal-code-gate required.
 *
 * Trigger modes:
 *  - "after_postal_code" → shows on the pageview AFTER the postal-code
 *      gate is completed. (Completing the gate triggers a full page
 *      reload, so we must show on the next load, not the same one.)
 *  - "delay" → shows after configurable page load delay
 *
 * WHY "next pageview":
 *  Completing the postal-code gate sets the `pcg_zone` cookie and then
 *  reloads the page (server-side redirect). Any popup shown in the same
 *  instant is destroyed by that navigation. So we detect the freshly-set
 *  zone on the FOLLOWING load and show then. A one-time marker cookie
 *  (`esp_seen`) ensures we only auto-show once, so returning visitors
 *  aren't nagged on every subsequent page.
 *
 * State cookies:
 *  - pcg_zone      (set by the gate)  → visitor has chosen a zone
 *  - esp_seen      (set by us)        → we've already auto-shown once
 *  - esp_dismissed (set by us)        → user closed/subscribed; suppress
 *
 * CAPTCHA strategy:
 *  The form submits NATIVELY. Shopify's CAPTCHA intercepts the submit
 *  and injects the token. A hidden return_to field redirects back with
 *  ?esp_success=1 so we can show a confirmation message.
 *
 * Flash fix:
 *  hide() ignores close events within OPEN_GUARD_MS of show(), and the
 *  overlay/close handlers stop propagation and only act on events
 *  targeted at the overlay itself — so a stray bubbled event can't
 *  close the popup the instant it opens.
 * ============================================================
 */

(function () {
  'use strict';

  const CONFIG = window.ESP_CONFIG || {
    trigger: 'after_postal_code',
    delay: 3,
    dismissDays: 30,
    buttonText: 'Subscribe',
    successMessage: 'Thanks for subscribing!'
  };

  const COOKIE_DISMISSED = 'esp_dismissed';
  const COOKIE_ZONE = 'pcg_zone';
  const COOKIE_SEEN = 'esp_seen';

  const OPEN_GUARD_MS = 700;
  let justShownAt = 0;

  /* ──────────────────────────────────────────────────
   * UTILITIES
   * ────────────────────────────────────────────────── */
  function setCookie(name, val, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + val + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  function getCookie(name) {
    const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? m[2] : null;
  }

  function isDismissed() {
    return !!getCookie(COOKIE_DISMISSED);
  }

  function dismiss() {
    setCookie(COOKIE_DISMISSED, '1', CONFIG.dismissDays || 30);
  }

  const $ = function (s) { return document.querySelector(s); };

  /* ──────────────────────────────────────────────────
   * SHOW / HIDE
   * ────────────────────────────────────────────────── */
  function show() {
    if (isDismissed()) return;
    var ov = $('#espOverlay');
    var popup = $('#espPopup');
    if (!ov || !popup) return;
    ov.classList.add('is-active');
    popup.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    justShownAt = Date.now();
    var inp = popup.querySelector('.esp-popup__input');
    if (inp) setTimeout(function () { inp.focus(); }, 350);
  }

  /**
   * Close the popup.
   * @param {boolean} userInitiated - true only when the user actively
   *   closed it. Only a real user close sets the dismiss cookie.
   */
  function hide(userInitiated) {
    if (Date.now() - justShownAt < OPEN_GUARD_MS) return;

    var ov = $('#espOverlay');
    var popup = $('#espPopup');
    if (!ov || !popup) return;
    ov.classList.remove('is-active');
    popup.classList.remove('is-active');
    document.body.style.overflow = '';

    if (userInitiated === true) dismiss();
  }

  /* ──────────────────────────────────────────────────
   * SUCCESS STATE (after redirect back)
   * ────────────────────────────────────────────────── */
  function showSuccessPopup() {
    var popup = $('#espPopup');
    var successEl = $('#espSuccess');
    var ov = $('#espOverlay');
    if (!popup || !successEl || !ov) return;

    var content = popup.querySelector('.esp-popup__content');
    if (content) {
      Array.from(content.children).forEach(function (child) {
        if (child !== successEl) child.style.display = 'none';
      });
      successEl.style.display = 'block';
    }

    ov.classList.add('is-active');
    popup.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    justShownAt = Date.now();

    dismiss();

    var url = new URL(window.location);
    url.searchParams.delete('esp_success');
    window.history.replaceState({}, '', url);

    setTimeout(function () { hide(false); }, 3000);
  }

  /* ──────────────────────────────────────────────────
   * FORM VALIDATION (client-side only)
   * ────────────────────────────────────────────────── */
  function validateForm(e) {
    var form = document.getElementById('esp-form');
    var emailInput = form ? form.querySelector('.esp-popup__input') : null;
    var errorEl = $('#espError');

    if (!form || !emailInput) return;

    var email = emailInput.value.trim();
    if (errorEl) errorEl.textContent = '';
    emailInput.classList.remove('has-error');

    if (!email) {
      e.preventDefault();
      if (errorEl) errorEl.textContent = 'Please enter your email address.';
      emailInput.classList.add('has-error');
      emailInput.focus();
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      e.preventDefault();
      if (errorEl) errorEl.textContent = 'Please enter a valid email address.';
      emailInput.classList.add('has-error');
      emailInput.focus();
      return;
    }

    var btn = $('#espSubmit');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Submitting\u2026';
    }
  }

  /* ──────────────────────────────────────────────────
   * TRIGGER: After Postal Code Gate (next-pageview)
   *
   * Completing the gate reloads the page, so we can't show in the
   * same instant. Instead:
   *   - If zone is set AND we haven't auto-shown yet (no esp_seen),
   *     this is the first load after the gate was completed → show,
   *     and set esp_seen so we don't show again on later pages.
   *   - If zone is NOT set yet, the visitor is still on/at the gate.
   *     Watch for the zone to appear; if the gate closes without a
   *     reload (some flows don't navigate), show on this same page.
   *     Either way esp_seen guards against double-showing.
   * ────────────────────────────────────────────────── */
  function setupPostalCodeTrigger() {
    var pcgModal = document.getElementById('pcgModal');

    // If PCG modal doesn't exist, fall back to delay trigger.
    if (!pcgModal) {
      setupDelayTrigger();
      return;
    }

    // Already auto-shown once for this visitor → don't nag again.
    if (getCookie(COOKIE_SEEN)) return;

    // CASE 1: Zone already set at load — this is the pageview right
    // after the gate was completed (the gate reloaded us here). Show
    // now, and mark as seen.
    if (getCookie(COOKIE_ZONE)) {
      setCookie(COOKIE_SEEN, '1', 365);
      // Short delay lets the page settle; OPEN_GUARD_MS protects the
      // open against any residual events.
      setTimeout(show, 600);
      return;
    }

    // CASE 2: No zone yet — visitor hasn't completed the gate. Poll
    // for the zone to appear in case the gate closes WITHOUT a full
    // reload (defensive; if it does reload, CASE 1 handles it on the
    // next load).
    var poll = setInterval(function () {
      if (getCookie(COOKIE_SEEN)) { clearInterval(poll); return; }
      if (!getCookie(COOKIE_ZONE)) return;
      // Zone appeared without navigating away yet. Wait for the gate
      // to be visually closed, then show once.
      if (pcgModal.classList.contains('is-active')) return;
      clearInterval(poll);
      setCookie(COOKIE_SEEN, '1', 365);
      setTimeout(show, 400);
    }, 200);
  }

  /* ──────────────────────────────────────────────────
   * TRIGGER: Delay
   * ────────────────────────────────────────────────── */
  function setupDelayTrigger() {
    setTimeout(show, (CONFIG.delay || 3) * 1000);
  }

  /* ──────────────────────────────────────────────────
   * BIND EVENTS
   * ────────────────────────────────────────────────── */
  function bindEvents() {
    var closeBtn = $('#espClose');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        hide(true);
      });
    }

    var form = document.getElementById('esp-form');
    if (form) {
      form.addEventListener('submit', validateForm);
    }

    var ov = $('#espOverlay');
    if (ov) {
      ov.addEventListener('click', function (e) {
        if (e.target !== ov) return;
        e.stopPropagation();
        hide(true);
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var popup = $('#espPopup');
        if (popup && popup.classList.contains('is-active')) {
          hide(true);
        }
      }
    });
  }

  /* ──────────────────────────────────────────────────
   * INIT
   * ────────────────────────────────────────────────── */
  function init() {
    bindEvents();

    var params = new URLSearchParams(window.location.search);
    if (params.get('esp_success') === '1') {
      showSuccessPopup();
      return;
    }

    if (isDismissed()) return;

    if (CONFIG.trigger === 'after_postal_code') {
      setupPostalCodeTrigger();
    } else if (CONFIG.trigger === 'delay') {
      setupDelayTrigger();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for theme editor testing.
  window.ESP = { show: show, hide: hide };
})();